import { ReactNode } from "react";

import { FormControl, Grid, InputAdornment } from "@mui/material";
import { AccordionKeys, DecimalTextField, FormikDecimalTextField, useActiveClaimsSummary } from "@shared";
import { FormikProps } from "formik";

import { ClientOverviewFormValues } from "@/pages/clients/client/components/tabs/overview-tab/OverviewTab.types";
import { useTranslation } from "@/translations";

import { ClientAccordion } from "../client-accordion/ClientAccordion";

const AMOUNT_DECIMAL_SCALE = 2;
const FIELD_FONT_SIZE = "0.875rem";

const euroAdornment = <InputAdornment position="end">€</InputAdornment>;

interface Props {
  clientId: string;
  formik: FormikProps<ClientOverviewFormValues>;
}

interface DerivedFieldProps {
  label: string;
  value: string | undefined;
  decimalScale: number;
  endAdornment?: ReactNode;
}

function DerivedField({ label, value, decimalScale, endAdornment }: DerivedFieldProps) {
  return (
    <FormControl fullWidth margin="none">
      <DecimalTextField
        label={label}
        value={value}
        onChange={() => undefined}
        helperText=" "
        size="small"
        disabled
        decimalScale={decimalScale}
        fixedDecimalScale
        slotProps={{
          inputLabel: { sx: { fontSize: FIELD_FONT_SIZE } },
          input: { sx: { fontSize: FIELD_FONT_SIZE }, endAdornment },
        }}
      />
    </FormControl>
  );
}

export function ClientDebtOverview({ clientId, formik }: Props) {
  const { t } = useTranslation();
  const summary = useActiveClaimsSummary(clientId);

  return (
    <ClientAccordion
      title={t("clients.client.overview.debt")}
      defaultExpanded
      accordionKey={AccordionKeys.client.indebtedness}
    >
      <Grid container rowSpacing={2} columnSpacing={2}>
        <Grid size={6}>
          <FormikDecimalTextField
            formik={formik}
            fieldName="estimatedDebt"
            label={t("clients.client.overview.estimatedDebt")}
            slotProps={{ input: { endAdornment: euroAdornment } }}
          />
        </Grid>
        <Grid size={6}>
          <DerivedField
            label={t("clients.client.overview.actualDebt")}
            value={summary?.totalSaldo.toFixed(AMOUNT_DECIMAL_SCALE)}
            decimalScale={AMOUNT_DECIMAL_SCALE}
            endAdornment={euroAdornment}
          />
        </Grid>
        <Grid size={6}>
          <FormikDecimalTextField
            formik={formik}
            fieldName="estimatedNumberOfCreditors"
            label={t("clients.client.overview.estimatedNumberOfCreditors")}
            decimalScale={0}
          />
        </Grid>
        <Grid size={6}>
          <DerivedField
            label={t("clients.client.overview.actualNumberOfCreditors")}
            value={summary?.count.toString()}
            decimalScale={0}
          />
        </Grid>
      </Grid>
    </ClientAccordion>
  );
}
